import {
    createSlice,
    createAsyncThunk,
    createEntityAdapter,
    createSelector,
} from '@reduxjs/toolkit'
import {
    getCollectionsList,
    createCollectionItem,
    updateCollectionItem,
    deleteCollectionItem,
} from './collectionApi'
import { axiosApi } from 'services/api'
import { generateQuery } from 'utils/filter'
import { transformCollections, transformCollection } from 'utils/transform'

const collectionAdapter = createEntityAdapter()
const collectionState = state => state.collections

export const collectionSelector = collectionAdapter.getSelectors(collectionState)
const collections = state => collectionSelector.selectAll(state)

export const getCollections = createAsyncThunk('collections/list', async (arg, thunkAPI) => {
    try {
        const { collections } = thunkAPI.getState()
        const query = generateQuery(collections.filterParams)
        const response = await getCollectionsList(query)
        return response.data
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

export const createCollection = createAsyncThunk('collection/create', async (arg, thunkAPI) => {
    try {
        const response = await createCollectionItem(arg)
        thunkAPI.dispatch(
            axiosApi.util.updateQueryData('getCollections', undefined, draft => {
                draft.unshift(response.data.data)
            })
        )
        return response.data
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

export const updateCollection = createAsyncThunk('collection/update', async (arg, thunkAPI) => {
    try {
        const response = await updateCollectionItem(arg)
        thunkAPI.dispatch(axiosApi.util.invalidateTags(['Collection', 'DisabledState']))
        return response.data
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

export const deleteCollection = createAsyncThunk('collection/delete', async (arg, thunkAPI) => {
    try {
        const response = await deleteCollectionItem(arg)
        thunkAPI.dispatch(axiosApi.util.invalidateTags(['Collection', 'DisabledState']))
        return response.data
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

export const initialFilter = { tag: ['all'], shared: false }
const initialFilterParams = [
    {
        type: 'sort',
        value: ['Sort.By=modifiedDate&Sort.Desc=true'],
    },
]

const emptyInitialState = collectionAdapter.getInitialState({
    selectedCollection: null,
    filter: initialFilter,
    uiFilter: 'all',
    sort: 'dateDesc',
    search: '',
    isSearched: false,
    isFiltered: false,
    isLoading: false,
    isCreating: false,
    isError: false,
    filterParams: initialFilterParams,
})

const filledState = collectionAdapter.upsertMany(emptyInitialState, [
    {
        name: 'All favorites',
        fileCount: 0,
        id: 'favorites',
        isNotList: true,
        updatedDateTime: null,
    },
    {
        name: 'Shared with me',
        fileCount: 0,
        id: 'shared',
        isNotList: true,
        updatedDateTime: null,
        isActive: true,
    },
])

const collectionSlice = createSlice({
    name: 'collections',
    initialState: emptyInitialState,
    reducers: {
        setCollection: (state, { payload }) => {
            state.selectedCollection = payload
        },
        setFilter: (state, { payload }) => {
            state.filter = payload
        },
        setUIFilter: (state, { payload }) => {
            state.uiFilter = payload
        },
        setSort: (state, { payload }) => {
            state.sort = payload
        },
        setSearch: (state, { payload }) => {
            state.isSearched = payload.status
            state.search = payload.keyword
        },
        resetFilter: state => {
            state.filter = initialFilter
            state.filterParams = initialFilterParams
            state.isFiltered = false
        },
        updateFilter: (state, { payload }) => {
            state.filter[payload.key] = payload.value
        },
        resetAll: state => {
            state.filter = initialFilter
            state.sort = 'dateDesc'
            state.isFiltered = false
        },
        clearSuggestion: state => {
            state.isSearched = false
            state.search = ''
        },
        setFilterParams: (state, { payload }) => {
            state.selectedCollection = null
            if (state.filterParams.findIndex(p => p.type === payload.type) < 0) {
                const data = [...state.filterParams, payload]
                state.filterParams = data
                state.isFiltered = Boolean(data.find(d => d.type === 'filter'))
            } else {
                if (payload.value && payload.value.length) {
                    const data = [
                        ...state.filterParams.filter(p => p.type !== payload.type),
                        payload,
                    ]
                    state.filterParams = data
                    state.isFiltered = Boolean(data.find(d => d.type === 'filter'))
                } else {
                    const data = state.filterParams.filter(p => p.type !== payload.type)
                    state.filterParams = data
                    state.isFiltered = Boolean(data.find(d => d.type === 'filter'))
                }
            }
        },
        patchCollection: (state, { payload }) => {
            const { id, ...rest } = payload

            collectionAdapter.updateOne(state, {
                id: payload.id,
                changes: {
                    ...state.entities[payload.id],
                    ...rest,
                },
            })
        },
        patchFileCount: (state, { payload }) => {
            const { id, fileCount, ...rest } = payload

            collectionAdapter.updateMany(state, [
                ...id.map(i => {
                    return {
                        id: i,
                        changes: {
                            ...state.entities[i],
                            fileCount: fileCount + state.entities[i].fileCount,
                            ...rest,
                        },
                    }
                }),
            ])
        },
        addOne: (state, { payload }) => {
            collectionAdapter.addOne(state, { ...transformCollection(payload), isNew: true })
        },
    },
    extraReducers: builder => {
        builder.addCase(getCollections.pending, state => {
            collectionAdapter.removeMany(state, [
                ...state.ids.filter(id => !['favorites', 'shared'].includes(id)),
            ])
            state.isLoading = true
            state.isError = false
        })
        builder.addCase(getCollections.fulfilled, (state, { payload }) => {
            collectionAdapter.addMany(state, transformCollections(payload.data))
            state.isLoading = false
        })
        builder.addCase(getCollections.rejected, state => {
            state.isLoading = false
            state.isError = true
        })
        builder.addCase(createCollection.pending, state => {
            state.isCreating = true
        })
        builder.addCase(createCollection.fulfilled, (state, { payload }) => {
            collectionAdapter.addOne(state, { ...transformCollection(payload.data), isNew: true })
            state.isCreating = false
        })
        builder.addCase(createCollection.rejected, state => {
            state.isCreating = false
        })
        builder.addCase(updateCollection.pending, state => {
            state.isLoading = true
        })
        builder.addCase(updateCollection.fulfilled, (state, { payload }) => {
            collectionAdapter.updateOne(state, { id: payload.data.id, changes: payload.data })
            state.isLoading = false
        })
        builder.addCase(updateCollection.rejected, state => {
            state.isLoading = false
        })
        builder.addCase(deleteCollection.pending, state => {
            state.isLoading = true
        })
        builder.addCase(deleteCollection.fulfilled, (state, { meta }) => {
            collectionAdapter.removeOne(state, meta.arg)
            state.isLoading = false
            state.selectedCollection = null
        })
        builder.addCase(deleteCollection.rejected, state => {
            state.isLoading = false
        })
    },
})

export const {
    setCollection,
    setFilter,
    setUIFilter,
    setSort,
    setSearch,
    updateFilter,
    resetFilter,
    resetAll,
    setFilterParams,
    clearSuggestion,
    addCollection,
    patchCollection,
    patchFileCount,
    addOne,
} = collectionSlice.actions

export const collectionsNames = createSelector(collections, collections => {
    return collections.map(c => c.name)
})

export default collectionSlice.reducer
